<template>
  <Transition name="fade"
              mode="out-in"
  >
    <div v-if="computedModelValue"
         aria-label="Modal window"
         class="modal-w"
         role="dialog"
    >
      <div ref="refModal"
           class="modal-content-w relative overflow-hidden"
           :style="{ maxWidth: widthContent + 'px' }"
           v-bind="$attrs"
      >
        <button v-if="showClose"
                aria-label="Closed"
                title="Closed"
                class="modal-close-btn cursor-pointer display-flex align-items-center justify-center"
                type="button"
                @click="closed('only-button')"
        >
          <IconMain icon="close"
                    :size="20"
          />
        </button>
        <slot></slot>
      </div>
      <div class="close-area"
           aria-label="Closed"
           title="Closed"
           @click="closed('only-area')"
      ></div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { computed, ref } from 'vue';

import { useModal } from '#imports';

defineOptions({
    inheritAttrs: false
});

const props = withDefaults(
    defineProps<{
        modelValue: boolean;
        closedMode?: 'only-button' | 'only-area' | 'all';
        widthContent?: number;
    }>(),
    {
        modelValue: false,
        closedMode: 'all',
        widthContent: 600
    }
);
const emit = defineEmits<{
    (e: 'update:modelValue', payload: boolean): void;
}>();
const refModal = ref<HTMLElement | null>(null);
const computedModelValue = computed({
    get() {
        return props.modelValue;
    },
    set(newValue) {
        emit(
            'update:modelValue',
            newValue
        );
    }
});

const showClose = computed(() => props.closedMode === 'only-button' || props.closedMode === 'all');

const closed = (mode: 'only-button' | 'only-area' | 'all') => {
    if (props.closedMode === mode || props.closedMode === 'all') closedPopUp();
};

const { closedPopUp } = useModal(
    computedModelValue,
    refModal,
    true
);

</script>

<style lang="scss" scoped>
.modal-w {
  display: flex;
  position: fixed;
  z-index: 9999;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  max-height: 100vh;
  inset: 0;
  background-color: rgb(0 0 0 / 50%);
}

.modal-content-w {
  display: flex;
  position: relative;
  z-index: 1;
  flex-direction: column;
  width: 100%;
  padding: 40px;
  border-radius: var(--radius-100, 4px);
  background-color: var(--layer-floor-2);
  gap: 8px;
}

.modal-close-btn {
  position: absolute;
  z-index: 15;
  top: 14px;
  right: 14px;
  width: 24px;
  height: 24px;
  padding: 0;
  transition: opacity 0.3s ease-in-out;
  border-radius: 50%;
  opacity: 1;

  svg {
    color: var(--content-primary)
  }
  // color: $c-gray-100;

  &:hover {
    opacity: 0.8;
  }
}

.close-area {
  position: absolute;
  width: 100%;
  height: 100%;
  opacity: 0.2;
  // background-color: $c-gray-00;
}

@include mobile-576 {
  .modal-w {
    display: flex;
    position: fixed;
    z-index: 9999;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    max-height: 100vh;
    padding: 8px;
    inset: 0;
  }

  .modal-content-w {
    display: flex;
    position: relative;
    z-index: 1;
    flex-direction: column;
    width: 100%;
    min-width: auto;
    padding: 24px;
    gap: 24px;
  }
}

.fade-enter-active {
  transition: all 0.3s ease-out;
}

.fade-leave-active {
  transition: all 0.3s cubic-bezier(1, 0.8, 0.9, 1);
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
